import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../config/api';
import {
  getCachedUserRawSync,
  getCustomerTokenSync,
  hydrateAuthTokens,
  setCachedUserRaw,
  setCustomerToken,
} from '../utils/authStorage';

interface User {
  id: string;
  email: string;
  name: string;
  phone?: string;
  points: number;
  wallet_balance: number;
  subscription_active: boolean;
  student_verified?: boolean;
  student_id_number?: string;
  isGuest?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, phone?: string) => Promise<void>;
  loginAsGuest: () => Promise<void>;
  logout: () => void;
  loading: boolean;
  updateUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function readCachedUser(): User | null {
  try {
    const raw = getCachedUserRawSync();
    if (!raw) return null;
    return normalizeUser(JSON.parse(raw) as User);
  } catch {
    return null;
  }
}

function persistCachedUser(user: User | null) {
  void setCachedUserRaw(user ? JSON.stringify(user) : null);
}

/** SQLite/PG send 0/1; React would render a leftover "0" if used in `flag && ...`. */
export function normalizeUser(raw: User | null | undefined): User | null {
  if (!raw) return null;
  return {
    ...raw,
    points: Number(raw.points) || 0,
    wallet_balance: Number(raw.wallet_balance) || 0,
    subscription_active: Boolean(raw.subscription_active),
  };
}

function applyAxiosAuth(token: string | null) {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const initialToken = getCustomerTokenSync();
  const initialCachedUser = initialToken ? readCachedUser() : null;
  const [user, setUser] = useState<User | null>(initialCachedUser);
  const [token, setToken] = useState<string | null>(initialToken);
  const [loading, setLoading] = useState(token ? !initialCachedUser : false);

  useEffect(() => {
    void (async () => {
      await hydrateAuthTokens();
      const stored = getCustomerTokenSync();
      if (stored && stored !== token) {
        applyAxiosAuth(stored);
        setToken(stored);
        if (!user) {
          const cached = readCachedUser();
          if (cached) setUser(cached);
        }
      }
    })();
    // Restore native Preferences on first mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (token) {
      applyAxiosAuth(token);
      const hasCachedUser = Boolean(readCachedUser());
      if (hasCachedUser) {
        setLoading(false);
      }
      fetchUser({ background: hasCachedUser });
    } else {
      try {
        localStorage.removeItem('guestUser');
      } catch {
        /* ignore */
      }
      persistCachedUser(null);
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async ({ background = false }: { background?: boolean } = {}) => {
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 12000)
      );

      const response = await Promise.race([
        axios.get('/api/auth/me'),
        timeoutPromise
      ]) as any;

      const me = normalizeUser(response.data as User);
      setUser(me);
      persistCachedUser(me);
    } catch (error: any) {
      console.error('Error fetching user:', error);

      const status = error?.response?.status;
      const msg = error?.response?.data?.error;
      // Missing Authorization is a client attach failure, not an expired session.
      // Keep the stored token so the next save/payment can still send it.
      if (status === 401 && msg === 'Access token required') {
        await hydrateAuthTokens();
        const stored = getCustomerTokenSync();
        if (stored) {
          applyAxiosAuth(stored);
          try {
            const retry = await axios.get('/api/auth/me');
            const me = normalizeUser(retry.data as User);
            setUser(me);
            persistCachedUser(me);
            return;
          } catch {
            // Fall through without wiping the session.
          }
        }
        return;
      }
      // Only clear session when the server rejected a token we actually sent.
      if ((status === 401 || status === 403) && getCustomerTokenSync()) {
        await setCustomerToken(null);
        applyAxiosAuth(null);
        setToken(null);
        persistCachedUser(null);

        try {
          const savedGuest = localStorage.getItem('guestUser');
          if (savedGuest) {
            const guest = normalizeUser(JSON.parse(savedGuest) as User);
            setUser(guest);
          } else {
            setUser(null);
          }
        } catch {
          setUser(null);
        }
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  };

  const applySession = async (newToken: string, newUser: User) => {
    const normalized = normalizeUser(newUser) as User;
    await setCustomerToken(newToken);
    applyAxiosAuth(newToken);
    persistCachedUser(normalized);
    setToken(newToken);
    setUser(normalized);
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      const { token: newToken, user: newUser } = response.data;
      await applySession(newToken, newUser as User);
    } catch (error: any) {
      console.error('Login API error:', error);
      if (error.response) {
        throw error;
      } else if (error.request) {
        throw new Error('Network error - cannot reach server. Please check if the server is running.');
      } else {
        throw new Error('Login failed. Please try again.');
      }
    }
  };

  const register = async (email: string, password: string, name: string, phone?: string) => {
    try {
      const response = await axios.post('/api/auth/register', { email, password, name, phone });
      const { token: newToken, user: newUser } = response.data;
      await applySession(newToken, newUser as User);
    } catch (error: any) {
      console.error('Register API error:', error);
      if (error.response) {
        throw error;
      } else if (error.request) {
        throw new Error('Network error - cannot reach server. Please check if the server is running.');
      } else {
        throw new Error('Registration failed. Please try again.');
      }
    }
  };

  const loginAsGuest = async () => {
    let lastError: any = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await axios.post(
          '/api/auth/guest-session',
          {},
          { timeout: 15000, __skipWakeRetry: true } as any
        );
        const { token: newToken, user: newUser } = response.data;
        try {
          localStorage.removeItem('guestUser');
        } catch {
          /* ignore */
        }
        await applySession(newToken, newUser as User);
        return;
      } catch (error: any) {
        lastError = error;
        const isTimeout =
          error?.code === 'ECONNABORTED' ||
          String(error?.message || '').toLowerCase().includes('timeout');
        if (!isTimeout || attempt === 1) {
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 1200));
      }
    }
    if (lastError) {
      throw lastError;
    }
    throw new Error('Guest login failed. Please try again.');
  };

  const logout = () => {
    const token = getCustomerTokenSync();
    if (token) {
      void axios.post('/api/auth/logout').catch(() => {});
    }
    setToken(null);
    setUser(null);
    void setCustomerToken(null);
    try {
      localStorage.removeItem('guestUser');
    } catch {
      /* ignore */
    }
    persistCachedUser(null);
    applyAxiosAuth(null);
  };

  const updateUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, loginAsGuest, logout, loading, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
